import { makeObservable, observable, transaction } from "mobx"

import {
  getClusterDetail,
  getClusters,
  getPairwiseVerdicts,
  rejectCluster,
} from "domains/match-clustering/api/match-clustering.call"
import { BaseStore } from "stores/base.store"
import { RootStore } from "stores/root.store"
import {
  ClusterDetail,
  ClusterSummary,
  ListClustersQuery,
  ListPairwiseVerdictsQuery,
  PairwiseVerdict,
  RejectClusterResponse,
} from "./types"

const DEFAULT_LIMIT = 50

function stripUndefined<T extends object>(obj: T): T {
  const out = {} as T
  for (const key of Object.keys(obj) as Array<keyof T>) {
    if (obj[key] !== undefined) {
      out[key] = obj[key]
    }
  }

  return out
}

export class MatchClusteringStore extends BaseStore {
  @observable
  public isLoading: boolean = false

  @observable
  public clusters: ClusterSummary[] = []

  @observable
  public total: number = 0

  @observable
  public currentQuery: ListClustersQuery = { limit: DEFAULT_LIMIT, offset: 0 }

  @observable
  public selectedCluster: ClusterDetail | null = null

  @observable
  public verdicts: PairwiseVerdict[] = []

  @observable
  public verdictsTotal: number = 0

  @observable
  public currentVerdictQuery: ListPairwiseVerdictsQuery = { limit: DEFAULT_LIMIT, offset: 0 }

  constructor(rootStore: RootStore) {
    super(rootStore)
    makeObservable(this)
  }

  public async loadClusters(query?: ListClustersQuery): Promise<void> {
    // Build from defaults + caller query (NOT currentQuery): otherwise a
    // Reset button calling loadClusters({ offset: 0, limit: DEFAULT_LIMIT })
    // can never clear stale filters. Strip undefined so the API call doesn't
    // serialize them as the literal string "undefined" in the URL.
    const nextQuery = stripUndefined<ListClustersQuery>({
      limit: DEFAULT_LIMIT,
      offset: 0,
      ...query,
    })
    const res = await this.defaultFunction(getClusters, nextQuery)
    transaction(() => {
      this.currentQuery = nextQuery
      this.clusters = res?.items ?? []
      this.total = res?.total ?? 0
    })
  }

  public async loadClusterDetail(id: string): Promise<void> {
    const res = await this.defaultFunction(getClusterDetail, id)
    transaction(() => {
      this.selectedCluster = res ?? null
    })
  }

  public async rejectCluster(id: string, reason: string): Promise<RejectClusterResponse | null> {
    const res = await this.defaultFunction(rejectCluster, id, reason)
    if (res?.outcome === "rejected") {
      await this.loadClusterDetail(id)
    }

    return res ?? null
  }

  public async loadPairwiseVerdicts(query?: ListPairwiseVerdictsQuery): Promise<void> {
    const nextQuery = stripUndefined<ListPairwiseVerdictsQuery>({
      limit: DEFAULT_LIMIT,
      offset: 0,
      ...query,
    })
    const res = await this.defaultFunction(getPairwiseVerdicts, nextQuery)
    transaction(() => {
      this.currentVerdictQuery = nextQuery
      this.verdicts = res?.items ?? []
      this.verdictsTotal = res?.total ?? 0
    })
  }
}
